import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCreditCard, FiSettings, FiMail, FiFileText, FiGift, FiDollarSign, FiCheck, FiX, FiEdit3 } = FiIcons;

const PaymentOptions = () => {
    const [paymentMethods, setPaymentMethods] = useState([
        { id: 1, name: 'Stripe', enabled: true, configured: true, description: 'Credit cards, digital wallets' },
        { id: 2, name: 'PayPal', enabled: true, configured: true, description: 'PayPal payments and subscriptions' },
        { id: 3, name: 'Square', enabled: false, configured: false, description: 'In-person and online payments' },
        { id: 4, name: 'Razorpay', enabled: false, configured: false, description: 'India-focused payment gateway' }
    ]);

    const [invoiceSettings, setInvoiceSettings] = useState({
        sendPdfInvoice: true,
        sendPaymentHistory: true,
        autoSend: true,
        companyName: 'Softvative Inc',
        companyAddress: '6385 Old Shady Oak Road, Suite 250\nEden Prairie, MN 55344, USA',
        taxId: 'US-123456789',
        footerText: 'Thank you for your business!'
    });

    const [couponSettings, setCouponSettings] = useState({
        enableCoupons: true,
        enableGiftCards: true,
        minimumOrderAmount: 10.00,
        maxDiscountPercent: 50,
        allowStackingDiscounts: false
    });

    const [isEditingInvoice, setIsEditingInvoice] = useState(false);
    const [isEditingCoupons, setIsEditingCoupons] = useState(false);

    const togglePaymentMethod = (methodId) => {
        setPaymentMethods(methods =>
            methods.map(method =>
                method.id === methodId
                    ? { ...method, enabled: !method.enabled }
                    : method
            )
        );
        const method = paymentMethods.find(m => m.id === methodId);
        toast.success(`${method.name} ${method.enabled ? 'disabled' : 'enabled'}`);
    };

    const configurePaymentMethod = (methodId) => {
        const method = paymentMethods.find(m => m.id === methodId);
        toast.info(`Opening ${method.name} configuration...`);
    };

    const saveInvoiceSettings = () => {
        toast.success('Invoice settings saved successfully!');
        setIsEditingInvoice(false);
    };

    const saveCouponSettings = () => {
        toast.success('Coupon settings saved successfully!');
        setIsEditingCoupons(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Payment Options
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300">
                        Configure payment methods, invoicing, and discount settings
                    </p>
                </motion.div>

                <div className="space-y-8">
                    {/* Payment Methods */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center mb-6">
                            <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Payment Methods
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {paymentMethods.map((method) => (
                                <div
                                    key={method.id}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                {method.name}
                                            </h3>
                                            <div className="ml-3 flex items-center space-x-2">
                                                {method.configured && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200">
                                                        <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                                                        Configured
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => togglePaymentMethod(method.id)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${method.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${method.enabled ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>

                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                        {method.description}
                                    </p>

                                    <button
                                        onClick={() => configurePaymentMethod(method.id)}
                                        className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                                    >
                                        <SafeIcon icon={FiSettings} className="w-4 h-4 mr-1" />
                                        Configure
                                    </button>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Invoice Settings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <SafeIcon icon={FiFileText} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Invoice & Email Settings
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsEditingInvoice(!isEditingInvoice)}
                                className="flex items-center px-4 py-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            >
                                <SafeIcon icon={FiEdit3} className="w-4 h-4 mr-2" />
                                {isEditingInvoice ? 'Cancel' : 'Edit'}
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Email Options */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Email Notifications
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">Send PDF Invoice</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Email PDF invoices to customers</p>
                                        </div>
                                        <button
                                            onClick={() => setInvoiceSettings({ ...invoiceSettings, sendPdfInvoice: !invoiceSettings.sendPdfInvoice })}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${invoiceSettings.sendPdfInvoice ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${invoiceSettings.sendPdfInvoice ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">Send Payment History</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Include payment history in emails</p>
                                        </div>
                                        <button
                                            onClick={() => setInvoiceSettings({ ...invoiceSettings, sendPaymentHistory: !invoiceSettings.sendPaymentHistory })}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${invoiceSettings.sendPaymentHistory ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${invoiceSettings.sendPaymentHistory ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Template */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Invoice Template
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Company Name
                                        </label>
                                        {isEditingInvoice ? (
                                            <input
                                                type="text"
                                                value={invoiceSettings.companyName}
                                                onChange={(e) => setInvoiceSettings({ ...invoiceSettings, companyName: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white">{invoiceSettings.companyName}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Tax ID
                                        </label>
                                        {isEditingInvoice ? (
                                            <input
                                                type="text"
                                                value={invoiceSettings.taxId}
                                                onChange={(e) => setInvoiceSettings({ ...invoiceSettings, taxId: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white">{invoiceSettings.taxId}</p>
                                        )}
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Company Address
                                        </label>
                                        {isEditingInvoice ? (
                                            <textarea
                                                rows={3}
                                                value={invoiceSettings.companyAddress}
                                                onChange={(e) => setInvoiceSettings({ ...invoiceSettings, companyAddress: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white whitespace-pre-line">{invoiceSettings.companyAddress}</p>
                                        )}
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Footer Text
                                        </label>
                                        {isEditingInvoice ? (
                                            <input
                                                type="text"
                                                value={invoiceSettings.footerText}
                                                onChange={(e) => setInvoiceSettings({ ...invoiceSettings, footerText: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white">{invoiceSettings.footerText}</p>
                                        )}
                                    </div>
                                </div>

                                {isEditingInvoice && (
                                    <div className="flex justify-end mt-4">
                                        <button
                                            onClick={saveInvoiceSettings}
                                            className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>

                    {/* Coupons & Discounts */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <SafeIcon icon={FiGift} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Coupons & Discounts
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsEditingCoupons(!isEditingCoupons)}
                                className="flex items-center px-4 py-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            >
                                <SafeIcon icon={FiEdit3} className="w-4 h-4 mr-2" />
                                {isEditingCoupons ? 'Cancel' : 'Edit'}
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Enable/Disable Options */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Enable Coupons</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Allow discount coupons</p>
                                    </div>
                                    <button
                                        onClick={() => setCouponSettings({ ...couponSettings, enableCoupons: !couponSettings.enableCoupons })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${couponSettings.enableCoupons ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                            }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${couponSettings.enableCoupons ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                        />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Enable Gift Cards</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Allow gift card purchases</p>
                                    </div>
                                    <button
                                        onClick={() => setCouponSettings({ ...couponSettings, enableGiftCards: !couponSettings.enableGiftCards })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${couponSettings.enableGiftCards ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                            }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${couponSettings.enableGiftCards ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            {/* Discount Settings */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Minimum Order Amount ($)
                                    </label>
                                    {isEditingCoupons ? (
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={couponSettings.minimumOrderAmount}
                                            onChange={(e) => setCouponSettings({ ...couponSettings, minimumOrderAmount: parseFloat(e.target.value) })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    ) : (
                                        <p className="text-gray-900 dark:text-white">${couponSettings.minimumOrderAmount.toFixed(2)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Maximum Discount (%)
                                    </label>
                                    {isEditingCoupons ? (
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={couponSettings.maxDiscountPercent}
                                            onChange={(e) => setCouponSettings({ ...couponSettings, maxDiscountPercent: parseInt(e.target.value) })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    ) : (
                                        <p className="text-gray-900 dark:text-white">{couponSettings.maxDiscountPercent}%</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">Allow Stacking Discounts</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Enable multiple discounts per order</p>
                                </div>
                                <button
                                    onClick={() => setCouponSettings({ ...couponSettings, allowStackingDiscounts: !couponSettings.allowStackingDiscounts })}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${couponSettings.allowStackingDiscounts ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${couponSettings.allowStackingDiscounts ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>

                            {isEditingCoupons && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={saveCouponSettings}
                                        className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default PaymentOptions;